import { useDispatch, useSelector } from "react-redux";
import { H3 } from "../../components/Heading";
import { authSelectors } from "../../store/auth/selector";
import {
  FirmAccountType,
  FirmQuery,
  FirmType,
  useAccountQuery,
  useCreateFirmMutation,
  useFirmQuery,
  useUpdateFirmMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import Loading from "../../components/Loading";
import { Card } from "../../components/Card";
import { Button } from "../../components/tailwind/Button";
import { AnimatedModal } from "../../components/AnimatedModal";
import { TextInput } from "../../components/tailwind/TextInput";
import { useState } from "react";
import { TextArea } from "../../components/tailwind/TextArea";
import { CloseIcon } from "../../components/CloseIcon";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import Dropdown, { Option } from "../../components/tailwind/Dropdown";
import { actions } from "../../store/auth/slice";
import { formatFirmType } from "@/src/utils/enums";
import { Route, Switch } from "react-router-dom";
import Members from "@/src/routes/firm/members";
import {
  BriefcaseIcon,
  TagIcon,
} from "lucide-react";
import { TabConfig } from "@/src/components/nav/types";
import FirmNavbar from "@/src/components/nav/firm-navbar";
import { FirmDealSettings } from "./deal_settings";
import { FirmRoleRestricted } from "@/src/components/FirmRoleRestricted";

const navLinks: TabConfig[] = [
  {
    label: "Home",
    icon: <BriefcaseIcon className="w-4 h-4 text-inherit" />,
    to: "/firm",
    exact: true,
  },
  {
    label: "Deal settings",
    icon: <TagIcon className="w-4 h-4" />,
    to: "/firm/deal-settings",
    firmAccountTypes: [FirmAccountType.Admin],
  },
];

export function Firm() {
  const account = useSelector(authSelectors.account);
  const client = useGqlClient();

  const [openModal, setOpenModal] = useState<"" | "edit_firm">("");

  const firmQuery = useFirmQuery(client, {
    id: account?.firm?.id ?? "",
  });

  if (!account) {
    return null;
  }

  const links = account.firm ? navLinks : navLinks.slice(0, 1);

  return (
    <div className="">
      <div className="flex p-4 px-6 items-center justify-between">
        {account.firm ? (
          <p className="font-semibold text-lg">{account.firm.name}</p>
        ) : null}
      </div>

      <Card margin="s xl">
        <FirmNavbar tabs={links} />
        <Switch>
          <Route path="/firm" exact>
            {account.firm ? (
              <div className="p-2">
                <div className="flex items-center gap-x-2 mb-2 ">
                  <p className="text-md font-semibold text-gray-900">Details</p>
                  <FirmRoleRestricted roles={[FirmAccountType.Admin]}>
                    <Button
                      text="Edit"
                      variant="neutral"
                      size="xs"
                      onClick={() => {
                        setOpenModal("edit_firm");
                      }}
                    />
                  </FirmRoleRestricted>
                </div>
                <FirmDetails firmQuery={firmQuery} />
                <p className="mb-2 mt-4 text-md font-semibold text-gray-900">
                  Members
                </p>
                <Members firmQuery={firmQuery} />
              </div>
            ) : (
              <CreateFirm />
            )}
          </Route>
          <Route path="/firm/deal-settings">
            <FirmDealSettings />
          </Route>
        </Switch>
      </Card>

      {firmQuery.data ? (
        <EditFirmModal
          firm={firmQuery.data.firm}
          open={openModal === "edit_firm"}
          onClose={() => {
            setOpenModal("");
          }}
        />
      ) : null}
    </div>
  );
}

function FirmDetails(props: { firmQuery: UseQueryResult<FirmQuery, unknown> }) {
  const [showEditModal, setShowEditModal] = useState(false);

  const { firmQuery } = props;

  if (firmQuery.error) {
    return (
      <div>
        <p>Something went wrong</p>
      </div>
    );
  }

  if (firmQuery.isPending || !firmQuery.data) {
    return <Loading />;
  }

  const firm = firmQuery.data.firm;

  return (
    <div>
      <div className="flex flex-col xl:flex-row  xl:gap-x-7">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-8 ">
          <div>
            <p className="font-medium text-xs text-gray-500">Name</p>
            <p className="text-sm font-semibold text-gray-900 ">{firm.name}</p>
          </div>
          <div>
            <p className="font-medium text-xs text-gray-500">Website</p>
            {firm.website ? (
              <div className="-mt-1">
                <a
                  target="_blank"
                  href={
                    firm.website.startsWith("http")
                      ? firm.website
                      : `https://${firm.website}`
                  }
                  className="text-sm leading-none font-semibold text-blue-600 "
                  rel="noreferrer"
                >
                  {firm.website}
                </a>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No website</p>
            )}
          </div>

          <div>
            <p className="font-medium text-xs text-gray-500">Address</p>
            {firm.address ? (
              <p className="text-sm text-gray-900 font-semibold">
                {firm.address}
              </p>
            ) : (
              <p className="text-sm text-gray-900 font-semibold">No address</p>
            )}
          </div>

          <div>
            <p className="font-medium text-xs text-gray-500">Type</p>
            <p className="text-sm text-gray-900 font-semibold">
              {formatFirmType(firm.type)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditFirmModal(props: {
  open: boolean;
  onClose: () => void;
  firm: FirmQuery["firm"];
}) {
  const queryClient = useQueryClient();
  const client = useGqlClient();
  const updateFirm = useUpdateFirmMutation(client);

  const [name, setName] = useState(props.firm.name);
  const [nameError, setNameError] = useState("");

  const [description, setDescription] = useState(props.firm.description);
  const [descriptionError, setDescriptionError] = useState("");

  const [selectedType, setSelectedType] = useState<Option>({
    value: props.firm.type,
    label: formatFirmType(props.firm.type),
  });

  const [website, setWebsite] = useState(props.firm.website);
  const [address, setAddress] = useState(props.firm.address);

  return (
    <AnimatedModal open={props.open} onClose={props.onClose}>
      <div>
        <div className="flex items-center justify-between">
          <H3>Edit {props.firm.name}</H3>
          <CloseIcon onClose={props.onClose} />
        </div>
        <form
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault();

            if (!name || !description) {
              if (!name) {
                setNameError("Name is required");
              }
              if (!description) {
                setDescriptionError("Description is required");
              }
              return;
            }

            updateFirm.mutate(
              {
                input: {
                  id: props.firm.id,
                  name,
                  description,
                  website,
                  address,
                  type: selectedType.value as FirmType,
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["Account", {}],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["Firm", { id: props.firm.id }],
                  });
                  props.onClose();
                },
              },
            );
          }}
        >
          <TextInput
            ignore1p
            label="Name"
            placeholder="Enter firm name..."
            value={name}
            onChange={(e) => {
              setNameError("");
              setName(e.currentTarget.value);
            }}
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-600">{nameError}</p>
          )}

          <label
            htmlFor="description"
            className="block text-sm mt-3  leading-6 text-gray-900 font-semibold"
          >
            Description
          </label>
          <TextArea
            label="Description"
            name="description"
            placeholder="A short description of your firm..."
            rows={4}
            value={description}
            onChange={(e) => [
              setDescriptionError(""),
              setDescription(e.currentTarget.value),
            ]}
          />
          {descriptionError && (
            <p className="mt-1 text-sm text-red-600">{descriptionError}</p>
          )}

          <label
            htmlFor="firm_type_dropdown"
            className="block text-sm mt-3 font-semibold leading-6 text-gray-900"
          >
            Firm type
          </label>
          <Dropdown
            options={options}
            onSelect={(o) => {
              setSelectedType(o);
            }}
            selectedOption={selectedType}
          />

          <TextInput
            margin="m 0 0 0"
            secondaryLabel="Optional"
            label="Website"
            placeholder="Enter firm website..."
            value={website}
            onChange={(e) => {
              setWebsite(e.currentTarget.value);
            }}
          />

          <TextInput
            ignore1p
            margin="m 0 0 0"
            label="Address"
            secondaryLabel="Optional"
            placeholder="Enter firm address..."
            value={address}
            onChange={(e) => {
              setAddress(e.currentTarget.value);
            }}
          />

          <div className="mt-6 flex justify-end">
            <Button
              isLoading={updateFirm.isPending}
              loadingText="Saving..."
              type="submit"
              variant="positive"
              text="Save"
            />
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}

function CreateFirm() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex p-4">
      <Card padding="m">
        <div className="h-full flex-1  flex flex-col">
          <p className="font-semibold text-lg text-gray-800">
            You don't have a firm
          </p>
          <p className="text-sm text-gray-500 leading-tight">
            Create one below or ask someone else at your Firm to add you
          </p>
        </div>
        <Button
          margin="xl 0 0 0"
          text="Create firm"
          variant="positive"
          onClick={() => {
            setOpen(true);
          }}
        />
      </Card>
      <CreateFirmModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </div>
  );
}

const options: Option[] = [
  {
    value: FirmType.LawFirm,
    label: formatFirmType(FirmType.LawFirm),
  },
  {
    value: FirmType.Accountant,
    label: formatFirmType(FirmType.Accountant),
  },
  {
    value: FirmType.Broker,
    label: formatFirmType(FirmType.Broker),
  },
  {
    value: FirmType.Buyer,
    label: formatFirmType(FirmType.Buyer),
  },
  {
    value: FirmType.Seller,
    label: formatFirmType(FirmType.Seller),
  },
  {
    value: FirmType.Other,
    label: formatFirmType(FirmType.Other),
  },
];

function CreateFirmModal(props: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const [website, setWebsite] = useState("");

  const [address, setAddress] = useState("");

  const [type, setType] = useState<Option>(options[0]);

  const queryClient = useQueryClient();
  const client = useGqlClient();
  const accountQuery = useAccountQuery(client);
  const createFirmMutation = useCreateFirmMutation(client);
  const dispatch = useDispatch();

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <H3>Create Firm</H3>
            <p className="text-sm text-gray-500 leading-tight">
              Create a new firm
            </p>
          </div>
          <CloseIcon onClose={props.onClose} />
        </div>

        <form
          className="mt-3 "
          onSubmit={(e) => {
            e.preventDefault();
            if (!name || !description) {
              if (!name) {
                setNameError("Name is required");
              }
              if (!description) {
                setDescriptionError("Description is required");
              }
              return;
            }

            createFirmMutation.mutate(
              {
                input: {
                  name,
                  description,
                  website,
                  address,
                  type: type.value as FirmType,
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["Account", {}],
                  });

                  accountQuery.refetch().then((data) => {
                    if (data.data) {
                      dispatch(
                        actions.refreshAccount({ account: data.data.account }),
                      );
                      props.onClose();
                    }
                  });
                },
              },
            );
          }}
        >
          <TextInput
            ignore1p
            label="Name"
            placeholder="Enter firm name..."
            onChange={(e) => {
              setNameError("");
              setName(e.currentTarget.value);
            }}
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-600">{nameError}</p>
          )}

          <label
            htmlFor="description"
            className="block text-sm mt-3 font-medium leading-6 text-gray-900"
          >
            Description
          </label>
          <TextArea
            label="Description"
            name="description"
            placeholder="A short description of your firm..."
            error={descriptionError}
            rows={2}
            value={description}
            onChange={(e) => {
              setDescriptionError("");
              setDescription(e.currentTarget.value);
            }}
          />
          {descriptionError && (
            <p className="mt-1 text-sm text-red-600">{descriptionError}</p>
          )}

          <label
            htmlFor="firm_type_dropdown"
            className="block text-sm mt-3 font-medium leading-6 text-gray-900"
          >
            Firm type
          </label>
          <Dropdown
            options={options}
            onSelect={(e) => {
              setType(e);
            }}
            selectedOption={type}
          />

          <TextInput
            margin="m 0 0 0"
            type="url"
            secondaryLabel="Optional"
            label="Website"
            placeholder="Enter firm website..."
            value={website}
            onChange={(e) => {
              setWebsite(e.currentTarget.value);
            }}
          />

          <TextInput
            ignore1p
            margin="m 0 0 0"
            label="Address"
            secondaryLabel="Optional"
            placeholder="Enter firm address..."
            value={address}
            onChange={(e) => {
              setAddress(e.currentTarget.value);
            }}
          />

          <div className="mt-6 flex justify-end">
            <Button
              variant="positive"
              text="Create firm"
              loadingText="Creating..."
              type="submit"
              isLoading={createFirmMutation.isPending}
            />
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}
